import {
  GetTransitDeclaration,
  GoodsClassification,
  GuaranteeData,
  IE3MasterConsignment,
  NonArrivedInvestigationInfo,
  PreviousDocument,
  PreviousDocumentExport,
  TransitData,
  TransitDeclaration,
  TransitGoodsItem,
  TransitHouseConsignment,
  TransitInspectionStatus,
  TransitPhase,
  TransitStatus,
  TransitSummary,
  TransportDetails,
  TransportDocument
} from "@portbase/bezoekschip-service-typescriptmodels";
import {AppContext} from "../app-context";
import {ComparatorChain} from '../common/comparator-chain';
import {VisitContext} from '../visit-details/visit-context';
import {lodash, nanoId, removeItem, sendQuery} from "../common/utils";
import moment from "moment";
import {map} from "rxjs/operators";
import guaranteeTypes from '@portbase/bezoekschip-service-typescriptmodels/refdata/transit-guaranteetypes.json';
import transitPreviousDocumentExportTypes
  from '@portbase/bezoekschip-service-typescriptmodels/refdata/transit-previousdocumentexporttypes.json';
import transitPreviousDocumentTypes
  from '@portbase/bezoekschip-service-typescriptmodels/refdata/transit-previousdocumenttypes.json';
import {QueryOptions} from "../common/query-gateway";

const transitPhases: TransitPhase[] = [TransitPhase.undeclared, TransitPhase.inspection,
  TransitPhase.insufficientGuarantee, TransitPhase.rejected, TransitPhase.releaseRejected, TransitPhase.declared,
  TransitPhase.sent, TransitPhase.cancellationRequested, TransitPhase.accepted, TransitPhase.inTransit,
  TransitPhase.inTransitMrnSent, TransitPhase.inTransitMrnRejected, TransitPhase.inTransitMrnAccepted,
  TransitPhase.incident, TransitPhase.proofOfArrivalRequested, TransitPhase.proofOfArrivalSent, TransitPhase.arrived,
  TransitPhase.discrepancies, TransitPhase.writtenOff, TransitPhase.cancelled];

export const findTransitClassifications = (term: string) =>
  sendQuery("com.portbase.bezoekschip.common.api.cargo.FindGoodsClassifications", {term: term, minimumCodeSize: 6});

export const transitStatusComparator: ComparatorChain =
  new ComparatorChain((a: Transit, b: Transit): number =>
      a.isNewTransitDeclaration ? b.isNewTransitDeclaration ? 0 : 1 : b.isNewTransitDeclaration ? -1 : 0,
    (a: Transit, b: Transit): number => ComparatorChain
      .compareValues(a.summary.sentDate || a.summary.sendDate, b.summary.sentDate || b.summary.sendDate, false, "+"),
    (a: Transit, b: Transit): number =>
      transitPhases.indexOf(a.summary.phase) - transitPhases.indexOf(b.summary.phase), 'summary.containerNumber');

export const transitItemComparator: ComparatorChain = transitStatusComparator.reverseProperties();

export function createEmptySummary(lrn: string): TransitSummary {
  const crn = VisitContext?.savedVisit?.crn;
  const declarant = AppContext.userProfile.organisation;

  if (!crn) {
    return <TransitSummary>{
      lrn: lrn,
      declarantShortName: declarant?.shortName,
      declarantFullName: declarant?.fullName,
      sendDate: moment().format('YYYY-MM-DD'),
      inspectionStatus: <TransitInspectionStatus>{},
      cancelInitiatedByCustoms: false,
      hasMultipleHouseConsignments: false,
      isInfoDeclared: false,
      isReleased: false,
      sendOnAta: false,
      phase: TransitPhase.undeclared
    };
  } else {
    return <TransitSummary>{
      lrn: lrn,
      crn: crn,
      declarantShortName: declarant?.shortName,
      declarantFullName: declarant?.fullName,
      inspectionStatus: <TransitInspectionStatus>{},
      cancelInitiatedByCustoms: false,
      hasMultipleHouseConsignments: false,
      isInfoDeclared: false,
      isReleased: false,
      sendOnAta: true,
      phase: TransitPhase.undeclared
    };
  }
}

export function updateSummary(transitSummary: TransitSummary, transitData: TransitData, declared: boolean): void {
  transitSummary.dischargeTerminalCode = transitData.dischargeTerminal?.terminalCode;
  transitSummary.dischargeTerminalName = transitData.dischargeTerminal?.terminalName;
  transitSummary.departureOfficeCode = transitData.departureOffice?.unCode;
  transitSummary.departureOfficeName = transitData.departureOffice?.name;
  transitSummary.destinationsOfficeCode = transitData.destinationsOffice?.unCode;
  transitSummary.destinationsOfficeName = transitData.destinationsOffice?.name;
  transitSummary.carrierRemarks = transitData.carrierRemarks;
  transitSummary.typeCode = transitData.type?.code;
  transitSummary.containerNumber = transitData.containerNumber;
  transitSummary.departureModeOfTransport = transitData.departureTransport?.modeOfTransport;
  transitSummary.hasMultipleHouseConsignments = transitData.houseConsignments.length > 1;
  transitSummary.transportTerm = transitData.transportTerm;
  transitSummary.phase = declared ? TransitPhase.declared : TransitPhase.undeclared;
}

export function initializeTransit(o: TransitDeclaration = undefined): TransitDeclaration {
  const input = o || <TransitDeclaration>{}
  const status = input.status || <TransitStatus>{};
  const result = input;

  result.declarantShortName = input.declarantShortName || AppContext.userProfile.organisation?.shortName;
  result.lrn = input.lrn || nanoId();
  result.crn = input.crn || (VisitContext.savedVisit ? VisitContext.savedVisit.crn : null);
  result.data = initializeTransitData(input.data)
  result.dataFromCustoms = initializeTransitData(input.dataFromCustoms)
  result.status = status;
  result.status.inspectionStatus = result.status.inspectionStatus || <TransitInspectionStatus>{};
  result.status.info = result.status.info || <NonArrivedInvestigationInfo>{};

  if (result.status.mrnSent && ["APMRTM", "APMII", "BCW", "RWG", "UNIP"]
    .some(t => t === result.data.dischargeTerminal?.organisationShortName)) {
    //we don't get accepts from these terminals (they're still using the legacy MID 1.0)
    result.status.mrnAccept = result.status.mrnSent;
  }

  if (!o && result.crn) {
    result.status.sendDate = null;
    result.status.sendOnAta = true;
  } else if (!status.sendDate || moment(status.sendDate).isBefore(moment())) {
    result.status.sendDate = moment().format('YYYY-MM-DD');
  }

  addEmptyGoodsItemAndGuaranteeIfNecessary(result.data);

  return result;
}

export function addEmptyGoodsItemAndGuaranteeIfNecessary(transitData: TransitData): void {
  if (transitData.goodsItems.length === 0 &&
    transitData.houseConsignments.length === 1 && transitData.houseConsignments[0].goodsItems.length === 0) {
    addEmptyGoodsItem(transitData);
  }

  if (transitData.guarantees.length === 0) {
    addEmptyGuarantee(transitData);
  }
}

export function initializeTransitData(o: TransitData = <TransitData>{}): TransitData {
  const data = o || <TransitData>{};

  data.houseConsignments = hasNonEmptyHouseConsignment(data.houseConsignments) ?
    data.houseConsignments : [<TransitHouseConsignment>{goodsItems: data.goodsItems || []}];
  data.transitOffices = data.transitOffices || [];
  data.departureTransport = data.departureTransport || <TransportDetails>{};
  data.borderTransport = data.borderTransport || <TransportDetails>{};
  data.bindingItinerary = data.bindingItinerary || false;
  data.transportTerm = <any>(typeof data.transportTerm == 'string' ?
    moment.duration(data.transportTerm).asSeconds() : data.transportTerm);
  data.seals = data.seals || [];
  data.itinerary = data.itinerary || [];
  data.guarantees = data.guarantees || [];

  copyHouseConsignmentsGoodsItemsToMasterGoodsItems(data);

  return data;
}

export function fixUpTransitGuaranteeBeforeSubmission(guaranteeData: GuaranteeData): GuaranteeData {
  const type = guaranteeData?.type;

  if (!type) {
    return null;
  } else {
    const fixedType = guaranteeTypes.find(guaranteeType => guaranteeType.code === type.code);

    if (!fixedType) {
      return null;
    } else {
      guaranteeData.type = fixedType;

      return guaranteeData;
    }
  }
}

export function fixUpTransitPreviousDocumentBeforeSubmission(previousDocument: PreviousDocument): PreviousDocument {
  const type = previousDocument?.type;

  if (!type) {
    return null;
  } else {
    const fixedType = transitPreviousDocumentTypes.find(documentType => documentType.code === type.code);

    if (!fixedType) {
      return null;
    } else {
      previousDocument.type = fixedType;

      return previousDocument;
    }
  }
}

function fixUpTransitData(input: TransitData): TransitData {
  const borderTransport = input.borderTransport;
  const output = Object.assign({}, input);

  if (input.houseConsignments.length <= 0) {
    input.houseConsignments.push(<TransitHouseConsignment>{goodsItems: output.goodsItems || []});
  } else {
    input.houseConsignments.forEach(houseConsignment => houseConsignment.goodsItems
      .filter(goodsItem => !output.goodsItems.some(item => item === goodsItem))
      .forEach(goodsItem => removeItem(houseConsignment.goodsItems, goodsItem)));

    input.houseConsignments.filter(houseConsignment => houseConsignment.goodsItems.length === 0)
      .forEach(houseConsignment => removeItem(input.houseConsignments, houseConsignment));

    if (input.houseConsignments.length <= 0) {
      input.houseConsignments.push(<TransitHouseConsignment>{goodsItems: []});
    }
  }

  if (!borderTransport.modeOfTransport && !borderTransport.nationality && !borderTransport.reference) {
    delete output.borderTransport;
  }

  delete output.goodsItems;

  return output;
}

export function fixUpTransitDataBeforeSaving(input: TransitData): TransitData {
  input.houseConsignments.forEach(houseConsignment => {
    const transportDocuments = new Set<TransportDocument>();

    houseConsignment.goodsItems.forEach(goodsItem => {
      if (!!goodsItem.transportDocuments) {
        goodsItem.transportDocuments.forEach(transportDocument => transportDocuments.add(transportDocument));
        delete goodsItem.transportDocuments;
      }
    });

    if (transportDocuments.size > 0) {
      houseConsignment.transportDocuments = Array.from(transportDocuments);
    }
  });

  return fixUpTransitData(input);
}

export function fixUpTransitDataBeforeDeclaration(input: TransitData): TransitData {
  var failure = false;

  input.houseConsignments.forEach(houseConsignment => {
    const transportDocuments = new Set<TransportDocument>();
    const exports = lodash.map(houseConsignment.goodsItems, isExport);

    if (exports.some(boolean => boolean) && exports.some(boolean => !boolean)) {
      AppContext.registerError("Either all or no goods in a house consignment must have an export previous document");

      failure = true;
    } else if (exports.some(boolean => boolean)) {
      const previousDocument = houseConsignment.goodsItems[0].previousDocument;
      const documentNumber = previousDocument?.documentNumber;
      const typeCode = previousDocument?.type?.code;

      if (houseConsignment.goodsItems.map(goodsItem => goodsItem.previousDocument).some(previousDocument =>
        previousDocument?.documentNumber !== documentNumber || previousDocument?.type?.code !== typeCode)) {
        AppContext.registerError("All goods in a house consignment must have the same export previous document");

        failure = true;
      } else {
        houseConsignment.goodsItems.forEach(goodsItem => delete goodsItem.previousDocument);
        houseConsignment.previousDocumentExport = previousDocument; // Why is this allowed? It does save a typecast...
      }
    } else if (houseConsignment.goodsItems.map(goodsItem => goodsItem.previousDocument)
      .filter(previousDocument => !!previousDocument).map(fixUpTransitPreviousDocumentBeforeSubmission)
      .some(previousDocument => !previousDocument)) {
      AppContext.registerError("Previous document type is not valid");

      failure = true;
    }

    houseConsignment.goodsItems.forEach(goodsItem => {
      if (!!goodsItem.transportDocuments) {
        goodsItem.transportDocuments.forEach(transportDocument => transportDocuments.add(transportDocument));
        delete goodsItem.transportDocuments;
      }
    });

    if (transportDocuments.size > 0) {
      houseConsignment.transportDocuments = Array.from(transportDocuments);
    }
  });

  if (failure) {
    return null;
  } else {
    input.guarantees.filter(guarantee => fixUpTransitGuaranteeBeforeSubmission(guarantee) === null)
      .forEach(guarantee => removeItem(input.guarantees, guarantee));

    const output = fixUpTransitData(input);

    if (output.houseConsignments.length > 0
      && output.houseConsignments.some(houseConsignment => houseConsignment.goodsItems.length > 0)) {
      return output;
    } else {
      AppContext.registerError("At least one goods item must be present");

      return null;
    }
  }
}

function isExport(goodsItem: TransitGoodsItem): boolean {
  return !!transitPreviousDocumentExportTypes.find(transitPreviousDocumentExportType =>
    transitPreviousDocumentExportType.code === goodsItem?.previousDocument?.type?.code);
}

export function copyHouseConsignmentsGoodsItemsToMasterGoodsItems(data: TransitData): void {
  const houseConsignments = data?.houseConsignments;

  if (houseConsignments && houseConsignments.length > 0) {
    data.goodsItems = lodash.flatMap(houseConsignments, houseConsignment => houseConsignment.goodsItems);
  }
}

export function undoIncompatibleTransitDataFixUps(data: TransitData): void {
  const houseConsignments = data.houseConsignments;

  if (houseConsignments.length > 0) {
    houseConsignments.forEach(houseConsignment => {
      const previousDocumentExport = houseConsignment.previousDocumentExport;
      const transportDocuments = houseConsignment.transportDocuments;

      if (!!previousDocumentExport) {
        houseConsignment.goodsItems.forEach(goodsItem => goodsItem.previousDocument = copy(previousDocumentExport));
        delete houseConsignment.previousDocumentExport;
      }

      if (!!transportDocuments) {
        if (transportDocuments.length > 0) {
          houseConsignment.goodsItems.forEach(goodsItem => goodsItem.transportDocuments = transportDocuments);
        }

        delete houseConsignment.transportDocuments;
      }
    });
  }
}

function copy(input: PreviousDocumentExport): PreviousDocument {
  const output = <PreviousDocument>{};

  output.type = input.type;
  output.documentNumber = input.documentNumber;

  return output;
}

export function formatSendDate(sendDate: string, sendOnAta: boolean): string {
  if (sendOnAta || !sendDate) {
    return null;
  } else {
    return moment(sendDate).format("YYYY-MM-DD") + "T00:00:00Z";
  }
}

function hasNonEmptyHouseConsignment(houseConsignments: TransitHouseConsignment[]): boolean {
  if (!houseConsignments || houseConsignments.length === 0) {
    return false;
  } else if (houseConsignments.length === 1) {
    return isNonEmpty(houseConsignments[0]);
  } else {
    return true;
  }
}

export function isNonEmpty(houseConsignment: TransitHouseConsignment): boolean {
  if (!houseConsignment) {
    return false;
  } else {
    const keys = Object.keys(houseConsignment);

    if (keys.length === 0) {
      return false;
    } else if (keys.length === 1 && keys[0] === "goodsItems") {
      const goodsItems = houseConsignment.goodsItems;

      if (goodsItems.length === 0) {
        return false;
      } else if (goodsItems.length === 1) {
        return isNonEmptyGoodsItem(goodsItems[0]);
      } else {
        return true;
      }
    } else {
      return true;
    }
  }
}

export function isNonEmptyGoodsItem(goodsItem: TransitGoodsItem): boolean {
  if (!goodsItem) {
    return false;
  } else {
    const keys = Object.keys(goodsItem);

    if (keys.length === 0) {
      return false;
    } else if (keys.length === 1 && keys[0] === "previousDocument") {
      return isNonEmptyPreviousDocument(goodsItem.previousDocument);
    } else {
      return true;
    }
  }
}

export function isNonEmptyPreviousDocument(previousDocument: PreviousDocument): boolean {
  return !!previousDocument && Object.keys(previousDocument).length > 0;
}

export function addEmptyGoodsItem(transitData: TransitData) {
  const emptyGoodsItem = <TransitGoodsItem>{
    previousDocument: <PreviousDocument>{}
  };

  if (transitData.houseConsignments.length === 1) {
    transitData.houseConsignments[0].goodsItems.splice(0, 0, emptyGoodsItem);
  }

  transitData.goodsItems.splice(0, 0, emptyGoodsItem);
}

export function addEmptyGuarantee(transitData: TransitData) {
  transitData.guarantees.push(<GuaranteeData>{
    type: null,
    country: {},
    customsValue: {
      currency: {}
    }
  });
}

export function getGoodsItems(masterConsignment: IE3MasterConsignment) {
  if (masterConsignment == null) {
    return null;
  } else {
    const masterConsignmentGoodsItems = masterConsignment.goodsItems;

    if (masterConsignmentGoodsItems != null && masterConsignmentGoodsItems.length > 0) {
      return masterConsignmentGoodsItems;
    } else {
      return lodash.flatMap(masterConsignment.houseConsignments, houseConsignment => houseConsignment.goodsItems);
    }
  }
}

export function loadTransit(transit: Transit): Promise<void> {
  if (!transit || transit.declaration) {
    return Promise.resolve();
  } else {
    return sendQuery("com.portbase.bezoekschip.common.api.transit.GetTransitDeclaration",
      <GetTransitDeclaration>{lrn: transit.summary.lrn},
      <QueryOptions>{caching: false, showSpinner: true, hideError: false})
      .forEach(transitDeclaration => {
        transit.declaration = initializeTransit(transitDeclaration);
        const transitData = transit.declaration.data;

        undoIncompatibleTransitDataFixUps(transitData);
        copyHouseConsignmentsGoodsItemsToMasterGoodsItems(transitData);
        copyHouseConsignmentsGoodsItemsToMasterGoodsItems(transit.declaration.dataFromCustoms);
      });
  }
}

export interface Transit {
  summary: TransitSummary;
  hidden: boolean;
  isContainerDeclaration: boolean;
  isNewTransitDeclaration: boolean;
  selected: boolean;
  declaration?: TransitDeclaration;
}
